import type { Playbook } from './types';

/**
 * The Playbook Vault.
 *
 * Downloadable execution blueprints that operationalise the case studies.
 * Ported from Realiform with one change: the `tier` field is gone. Realiform
 * gated three of these behind a paid Pro tier and one behind Builder; there is
 * no membership on KoreanHub, so everything here is free.
 *
 * `assetPath` is null on every entry because the documents have not been
 * produced yet — it was null in the source too. The UI says "not yet
 * available" rather than linking at a file that would 404, which is the only
 * honest rendering of a written-but-unbuilt asset.
 */

export const PLAYBOOKS: Playbook[] = [
  {
    slug: "evaluating-a-local-route-business",
    title: "Evaluating a local route business",
    summary:
      "The diligence sequence for vending, laundry, and service routes — including the utility-bill cross-check that catches inflated revenue claims.",
    format: "Checklist",
    pages: 14,
    relatedCaseStudySlugs: ["vending-route-suburban", "laundromat-seller-financed"],
    assetPath: null,
    includes: [
      "Per-machine revenue verification worksheet",
      "Utility-usage cross-check method",
      "Location agreement transfer script",
      "Equipment age and replacement schedule template",
      "Walk-away triggers",
    ],
  },
  {
    slug: "packaging-and-selling-a-web-utility",
    title: "How to package and sell a basic web utility",
    summary:
      "Taking a single-purpose tool from working code to paid product: pricing, billing, support load, and the first hundred customers.",
    format: "Blueprint",
    pages: 22,
    relatedCaseStudySlugs: ["micro-saas-invoice-tool"],
    assetPath: null,
    includes: [
      "Niche-selection scoring sheet",
      "Pre-sale validation script",
      "Pricing ladder with worked examples",
      "Stripe + auth wiring checklist",
      "Support-load estimate by price point",
    ],
  },
  {
    slug: "seller-financing-negotiation",
    title: "Negotiating seller financing",
    summary:
      "How to structure a note when the seller's real motivation is a clean exit — term sheets, security, and the terms worth trading price for.",
    format: "Template",
    pages: 18,
    relatedCaseStudySlugs: ["laundromat-seller-financed"],
    assetPath: null,
    includes: [
      "Term sheet template",
      "Rate-versus-price trade model",
      "Standby and subordination basics",
      "Seller motivation discovery questions",
      "Personal guarantee negotiation points",
    ],
  },
  {
    slug: "sde-diligence-workbook",
    title: "SDE diligence workbook",
    summary:
      "Rebuild a seller's earnings claim from source documents, and pressure-test every add-back before it reaches your valuation.",
    format: "Spreadsheet",
    pages: 9,
    relatedCaseStudySlugs: ["laundromat-seller-financed", "micro-saas-invoice-tool"],
    assetPath: null,
    includes: [
      "Add-back justification register",
      "Bank statement reconciliation tab",
      "Owner-replacement cost estimator",
      "Customer concentration analysis",
      "Quality-of-earnings summary output",
    ],
  },
  {
    slug: "solo-entity-setup",
    title: "Solo operator entity setup",
    summary:
      "LLC, S-corp election, and the compliance calendar that keeps the structure defensible once you have it.",
    format: "Checklist",
    pages: 16,
    relatedCaseStudySlugs: ["solo-consultant-entity-structure"],
    assetPath: null,
    includes: [
      "Crossover analysis worksheet",
      "Reasonable compensation documentation method",
      "Annual compliance calendar",
      "Bookkeeping separation checklist",
      "Questions to bring to your CPA",
    ],
  },
  {
    slug: "first-hundred-local-customers",
    title: "The first hundred local customers",
    summary:
      "Channel-by-channel acquisition for service businesses, ranked by cost per customer rather than by how modern it sounds.",
    format: "Blueprint",
    pages: 24,
    relatedCaseStudySlugs: ["pressure-washing-weekend"],
    assetPath: null,
    includes: [
      "Channel cost-per-customer benchmarks",
      "Route density planning method",
      "Google Business Profile optimisation steps",
      "Referral mechanics that do not need an app",
      "Pricing-up script for existing customers",
    ],
  },
];

export const PLAYBOOKS_BY_SLUG: Record<string, Playbook> = Object.fromEntries(
  PLAYBOOKS.map((p) => [p.slug, p])
);

export function getPlaybookBySlug(slug: string | undefined): Playbook | undefined {
  return slug ? PLAYBOOKS_BY_SLUG[slug] : undefined;
}

/** Playbooks that operationalise a given case study. */
export function playbooksForCaseStudy(slug: string): Playbook[] {
  return PLAYBOOKS.filter((p) => p.relatedCaseStudySlugs.includes(slug));
}
